import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { Ban, Pencil, History, FileWarning } from "lucide-react";
import { toast } from "sonner";
import { dateTimeInputForZone, getPreferences, zonedDateTimeToIso } from "@/lib/preferences";

const currentMonthKey = () => {
  const preferences = getPreferences();
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: preferences.timezone, year: "numeric", month: "2-digit" }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}`;
};

const monthDateRange = (monthKey) => {
  const [year, month] = String(monthKey || "").split("-").map(Number);
  if (!year || !month) return {};
  const start = `${year}-${String(month).padStart(2, "0")}-01`;
  const endDate = new Date(Date.UTC(year, month, 0));
  const end = `${endDate.getUTCFullYear()}-${String(endDate.getUTCMonth() + 1).padStart(2, "0")}-${String(endDate.getUTCDate()).padStart(2, "0")}`;
  return { start_date: start, end_date: end };
};

const transactionTimestamp = (transaction) => {
  const primary = new Date(transaction.transaction_date).getTime();
  const secondary = new Date(transaction.created_at).getTime();
  return [Number.isNaN(primary) ? 0 : primary, Number.isNaN(secondary) ? 0 : secondary];
};

const toDateTimeInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  return dateTimeInputForZone(date, getPreferences().timezone);
};

const correctionDataFor = (transaction, type) => {
  if (!transaction || type === "anular") return {};
  if (type === "monto") return { usdt_amount: transaction.usdt_amount };
  if (type === "precio") return { rate_dop: transaction.rate_dop };
  if (type === "porcentaje") return { percentage: transaction.percentage };
  if (type === "total") return { total_amount: transaction.total_amount };
  if (type === "moneda") return { currency: transaction.currency };
  if (type === "memo") return { memo: transaction.memo || "" };
  if (type === "fecha") return { transaction_date: toDateTimeInput(transaction.transaction_date) };
  return {
    usdt_amount: transaction.usdt_amount,
    total_amount: transaction.total_amount,
    memo: transaction.memo || "",
    ...(transaction.market_type === "detalle" ? { rate_dop: transaction.rate_dop } : { percentage: transaction.percentage }),
  };
};

export default function Transactions() {
  const { user, isAdmin, isOperator, isOpMayor, isOpDetalle } = useAuth();
  const [list, setList] = useState([]);
  const [filterMarket, setFilterMarket] = useState("todos");
  const [filterOp, setFilterOp] = useState("todas");
  const [filterStatus, setFilterStatus] = useState("activa");
  const [filterUser, setFilterUser] = useState("todos");
  const [filterClient, setFilterClient] = useState("todos");
  const [filterMonth, setFilterMonth] = useState(currentMonthKey);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [annulTxn, setAnnulTxn] = useState(null);
  const [annulReason, setAnnulReason] = useState("");
  const [editTxn, setEditTxn] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editReason, setEditReason] = useState("");
  const [historyTxn, setHistoryTxn] = useState(null);
  const [history, setHistory] = useState([]);
  const [corrTxn, setCorrTxn] = useState(null);
  const [corrForm, setCorrForm] = useState({ request_type: "monto", proposed_data: {}, reason: "" });

  const load = useCallback(async () => {
    const params = {};
    if (filterMarket !== "todos" && !isOperator) params.market = filterMarket;
    if (filterOp !== "todas") params.operation = filterOp;
    if (filterStatus !== "todas") params.status = filterStatus;
    if (filterUser !== "todos") params.created_by = filterUser;
    if (filterClient !== "todos") params.client_id = filterClient;
    Object.assign(params, monthDateRange(filterMonth));
    params.limit = 2000;
    const { data } = await api.get("/transactions", { params });
    const rows = (Array.isArray(data) ? data : [])
      .sort((left, right) => {
        const [rightDate, rightCreated] = transactionTimestamp(right);
        const [leftDate, leftCreated] = transactionTimestamp(left);
        return rightDate - leftDate || rightCreated - leftCreated;
      });
    setList(rows);
  }, [filterClient, filterMarket, filterMonth, filterOp, filterStatus, filterUser, isOperator]);

  const loadFilters = async () => {
    const [u, c, currenciesResponse, referrersResponse] = await Promise.all([
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/clients").catch(() => ({ data: [] })),
      api.get("/currencies").catch(() => ({ data: [] })),
      api.get("/referrers").catch(() => ({ data: [] })),
    ]);
    setUsers(Array.isArray(u.data) ? u.data : []);
    setClients(Array.isArray(c.data) ? c.data : []);
    setCurrencies(Array.isArray(currenciesResponse.data) ? currenciesResponse.data : []);
    setReferrers(Array.isArray(referrersResponse.data) ? referrersResponse.data : []);
  };

  useEffect(() => { loadFilters(); }, []);
  useEffect(() => { load(); }, [load]);

  const doAnnul = async () => {
    if (!annulReason.trim()) { toast.error("Motivo obligatorio"); return; }
    try { await api.post(`/transactions/${annulTxn.id}/annul`, { reason: annulReason }); toast.success("Anulada"); setAnnulTxn(null); setAnnulReason(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openEdit = (t) => {
    setEditTxn(t);
    setEditForm({ ...t, transaction_date: toDateTimeInput(t.transaction_date) });
    setEditReason("");
  };
  const doEdit = async () => {
    if (!editReason.trim()) { toast.error("Motivo obligatorio"); return; }
    try {
      const payload = {
        operation_type: editForm.operation_type, market_type: editForm.market_type, currency: editForm.currency,
        usdt_amount: parseFloat(editForm.usdt_amount),
        rate_dop: editForm.rate_dop !== null && editForm.rate_dop !== "" ? parseFloat(editForm.rate_dop) : null,
        percentage: editForm.percentage !== null && editForm.percentage !== "" ? parseFloat(editForm.percentage) : null,
        total_amount: parseFloat(editForm.total_amount), total_currency: editForm.total_currency,
        memo: editForm.memo, transaction_date: zonedDateTimeToIso(editForm.transaction_date, getPreferences().timezone),
        client_id: editForm.client_id || null, referrer_id: editForm.referrer_id || null,
        edit_reason: editReason,
      };
      await api.put(`/transactions/${editTxn.id}`, payload);
      toast.success("Editada"); setEditTxn(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openHistory = async (t) => { setHistoryTxn(t); const { data } = await api.get(`/transactions/${t.id}/history`); setHistory(data); };

  const openCorrection = (t) => {
    setCorrTxn(t);
    setCorrForm({ request_type: "monto", proposed_data: correctionDataFor(t, "monto"), reason: "" });
  };
  const changeCorrectionType = (requestType) => {
    setCorrForm((current) => ({ ...current, request_type: requestType, proposed_data: correctionDataFor(corrTxn, requestType) }));
  };
  const doCorrection = async () => {
    if (!corrForm.reason.trim()) { toast.error("Motivo obligatorio"); return; }
    try {
      const proposedData = { ...corrForm.proposed_data };
      if (corrForm.request_type === "fecha" && proposedData.transaction_date) {
        proposedData.transaction_date = zonedDateTimeToIso(proposedData.transaction_date, getPreferences().timezone);
      }
      await api.post("/corrections", { transaction_id: corrTxn.id, request_type: corrForm.request_type, proposed_data: proposedData, reason: corrForm.reason });
      toast.success("Solicitud enviada al admin");
      setCorrTxn(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Transacciones</h1>
        <p className="text-slate-500 mt-1">
          {isOpMayor && "Mercado Mayor - todas las transacciones"}
          {isOpDetalle && "Mercado Detalle - todas las transacciones"}
          {isAdmin && "Lista completa con acciones de admin"}
        </p>
      </div>

      <Card className="p-4 border border-slate-200 rounded-xl bg-white shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Mes</Label>
            <Input type="month" value={filterMonth} onChange={(event) => setFilterMonth(event.target.value)} className="mt-2" data-testid="filter-month" />
          </div>
          {!isOperator && (
            <div>
              <Label className="text-xs uppercase font-semibold text-slate-500">Mercado</Label>
              <Select value={filterMarket} onValueChange={setFilterMarket}>
                <SelectTrigger data-testid="filter-market" className="mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="detalle">Detalle</SelectItem>
                  <SelectItem value="mayor">Mayor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Operación</Label>
            <Select value={filterOp} onValueChange={setFilterOp}>
              <SelectTrigger data-testid="filter-op" className="mt-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="compra">Compra</SelectItem>
                <SelectItem value="venta">Venta</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Estado</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="filter-status" className="mt-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="activa">Activas</SelectItem>
                <SelectItem value="anulada">Anuladas</SelectItem>
                <SelectItem value="todas">Todas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Operador</Label>
            <Select value={filterUser} onValueChange={setFilterUser}>
              <SelectTrigger data-testid="filter-user" className="mt-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Cliente (mes actual)</Label>
            <Select value={filterClient} onValueChange={setFilterClient}>
              <SelectTrigger data-testid="filter-client" className="mt-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="transactions-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-3 text-left font-semibold">Fecha</th>
                <th className="px-3 py-3 text-left font-semibold">Mercado</th>
                <th className="px-3 py-3 text-left font-semibold">Op</th>
                <th className="px-3 py-3 text-left font-semibold">Moneda</th>
                <th className="px-3 py-3 text-right font-semibold">USDT</th>
                <th className="px-3 py-3 text-right font-semibold">Precio/%</th>
                <th className="px-3 py-3 text-right font-semibold">Total</th>
                <th className="px-3 py-3 text-left font-semibold">Cliente/Ref</th>
                <th className="px-3 py-3 text-left font-semibold">Operador</th>
                <th className="px-3 py-3 text-left font-semibold">Estado</th>
                <th className="px-3 py-3 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody className="font-mono tabular-nums">
              {list.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-slate-400">Sin transacciones</td></tr>
              )}
              {list.map((t) => (
                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`txn-row-${t.id}`}>
                  <td className="px-3 py-3 text-slate-600">{fmt.date(t.transaction_date)}</td>
                  <td className="px-3 py-3 capitalize">{t.market_type}</td>
                  <td className="px-3 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${t.operation_type === "compra" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{t.operation_type}</span>
                  </td>
                  <td className="px-3 py-3 font-semibold">{t.currency}</td>
                  <td className="px-3 py-3 text-right">{fmt.usdt(t.usdt_amount)}</td>
                  <td className="px-3 py-3 text-right">{t.market_type === "detalle" ? fmt.dop(t.rate_dop) : fmt.pct(t.percentage)}</td>
                  <td className="px-3 py-3 text-right">{fmt.usdt(t.total_amount)} <span className="text-xs text-slate-500">{t.total_currency}</span></td>
                  <td className="px-3 py-3 font-sans text-xs">
                    {t.client_name && <div className="text-slate-700">{t.client_name}</div>}
                    {t.referrer_name && <div className="text-emerald-600">ref: {t.referrer_name}</div>}
                  </td>
                  <td className="px-3 py-3 font-sans text-xs text-slate-600">{t.created_by_name || "-"}</td>
                  <td className="px-3 py-3">
                    <Badge className={t.status === "activa" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-rose-100 text-rose-700"}>{t.status}</Badge>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openHistory(t)} title="Historial" data-testid={`history-${t.id}`} className="p-1.5 rounded hover:bg-slate-200 text-slate-600"><History size={14} /></button>
                      {isAdmin && t.status === "activa" && !t.system_generated && (
                        <>
                          <button onClick={() => openEdit(t)} title="Editar" data-testid={`edit-${t.id}`} className="p-1.5 rounded hover:bg-slate-200 text-slate-600"><Pencil size={14} /></button>
                          <button onClick={() => setAnnulTxn(t)} title="Anular" data-testid={`annul-${t.id}`} className="p-1.5 rounded hover:bg-rose-100 text-rose-600"><Ban size={14} /></button>
                        </>
                      )}
                      {isOperator && t.status === "activa" && !t.system_generated && t.created_by === user?.id && (
                        <button onClick={() => openCorrection(t)} title="Solicitar corrección" data-testid={`correct-${t.id}`} className="p-1.5 rounded hover:bg-amber-100 text-amber-600"><FileWarning size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Annul Dialog (admin) */}
      <Dialog open={!!annulTxn} onOpenChange={(o) => !o && setAnnulTxn(null)}>
        <DialogContent data-testid="annul-dialog">
          <DialogHeader><DialogTitle>Anular transacción</DialogTitle><DialogDescription>Motivo obligatorio.</DialogDescription></DialogHeader>
          <Textarea value={annulReason} onChange={(e) => setAnnulReason(e.target.value)} placeholder="Motivo" data-testid="annul-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnnulTxn(null)}>Cancelar</Button>
            <Button onClick={doAnnul} className="bg-rose-600 hover:bg-rose-700" data-testid="annul-confirm">Anular</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog (admin) */}
      <Dialog open={!!editTxn} onOpenChange={(o) => !o && setEditTxn(null)}>
        <DialogContent data-testid="edit-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar transacción</DialogTitle><DialogDescription>Motivo obligatorio.</DialogDescription></DialogHeader>
          {editTxn && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Operacion</Label><Select value={editForm.operation_type} onValueChange={(operation_type) => setEditForm({ ...editForm, operation_type })}><SelectTrigger data-testid="edit-operation"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="compra">Compra</SelectItem><SelectItem value="venta">Venta</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Mercado</Label><Select value={editForm.market_type} onValueChange={(market_type) => setEditForm({ ...editForm, market_type })}><SelectTrigger data-testid="edit-market"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="detalle">Detalle</SelectItem><SelectItem value="mayor">Mayor</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Moneda</Label><Select value={editForm.currency} onValueChange={(currency) => setEditForm({ ...editForm, currency })}><SelectTrigger data-testid="edit-currency"><SelectValue /></SelectTrigger><SelectContent>{currencies.filter((currency) => currency.active !== false).map((currency) => <SelectItem key={currency.id || currency.symbol} value={currency.symbol}>{currency.symbol}</SelectItem>)}</SelectContent></Select></div>
              <div>
                <Label className="text-xs">Monto USDT</Label>
                <Input type="number" step="0.01" value={editForm.usdt_amount || ""} onChange={(e) => setEditForm({ ...editForm, usdt_amount: e.target.value })} />
              </div>
              {editForm.market_type === "detalle" ? (
                <div><Label className="text-xs">Precio DOP</Label>
                  <Input type="number" step="0.0001" value={editForm.rate_dop ?? ""} onChange={(e) => setEditForm({ ...editForm, rate_dop: e.target.value })} /></div>
              ) : (
                <div><Label className="text-xs">Porcentaje %</Label>
                  <Input type="number" step="0.0001" value={editForm.percentage ?? ""} onChange={(e) => setEditForm({ ...editForm, percentage: e.target.value })} /></div>
              )}
              <div><Label className="text-xs">Total</Label>
                <Input type="number" step="0.01" value={editForm.total_amount || ""} onChange={(e) => setEditForm({ ...editForm, total_amount: e.target.value })} /></div>
              <div><Label className="text-xs">Moneda del total</Label><Select value={editForm.total_currency} onValueChange={(total_currency) => setEditForm({ ...editForm, total_currency })}><SelectTrigger data-testid="edit-total-currency"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="DOP">DOP</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="USDT">USDT</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Cliente</Label><Select value={editForm.client_id || "none"} onValueChange={(client_id) => setEditForm({ ...editForm, client_id: client_id === "none" ? null : client_id })}><SelectTrigger data-testid="edit-client"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Sin cliente</SelectItem>{clients.map((client) => <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Referidor</Label><Select value={editForm.referrer_id || "none"} onValueChange={(referrer_id) => setEditForm({ ...editForm, referrer_id: referrer_id === "none" ? null : referrer_id })}><SelectTrigger data-testid="edit-referrer"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Sin referidor</SelectItem>{referrers.map((referrer) => <SelectItem key={referrer.id} value={referrer.id}>{referrer.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="col-span-2"><Label className="text-xs">Memo</Label>
                <Input value={editForm.memo || ""} onChange={(e) => setEditForm({ ...editForm, memo: e.target.value })} /></div>
              <div className="col-span-2"><Label className="text-xs">Fecha y hora de la transaccion</Label>
                <Input type="datetime-local" value={editForm.transaction_date || ""} onChange={(e) => setEditForm({ ...editForm, transaction_date: e.target.value })} data-testid="edit-transaction-date" /></div>
              <div className="col-span-2"><Label className="text-xs">Motivo *</Label>
                <Textarea value={editReason} onChange={(e) => setEditReason(e.target.value)} data-testid="edit-reason-input" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTxn(null)}>Cancelar</Button>
            <Button onClick={doEdit} className="bg-slate-900" data-testid="edit-confirm">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Correction Request (operator) */}
      <Dialog open={!!corrTxn} onOpenChange={(o) => !o && setCorrTxn(null)}>
        <DialogContent data-testid="correction-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Solicitar corrección</DialogTitle>
            <DialogDescription>El admin revisará y aprobará/rechazará. No afecta balances hasta su aprobación.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Tipo de solicitud</Label>
              <Select value={corrForm.request_type} onValueChange={changeCorrectionType}>
                <SelectTrigger data-testid="corr-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monto">Cambiar monto USDT</SelectItem>
                  {corrTxn?.market_type === "detalle" && <SelectItem value="precio">Cambiar precio/tasa</SelectItem>}
                  {corrTxn?.market_type === "mayor" && <SelectItem value="porcentaje">Cambiar porcentaje</SelectItem>}
                  <SelectItem value="total">Cambiar total</SelectItem>
                  <SelectItem value="moneda">Cambiar moneda</SelectItem>
                  <SelectItem value="memo">Cambiar memo</SelectItem>
                  <SelectItem value="fecha">Cambiar fecha y hora</SelectItem>
                  <SelectItem value="anular">Anular transacción</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {corrForm.request_type !== "anular" && (
              <div className="grid grid-cols-1 gap-3">
                {(corrForm.request_type === "monto" || corrForm.request_type === "otro") && <div>
                  <Label className="text-xs">Monto propuesto (USDT)</Label>
                  <Input type="number" step="0.01" value={corrForm.proposed_data.usdt_amount ?? ""} onChange={(e) => setCorrForm({ ...corrForm, proposed_data: { ...corrForm.proposed_data, usdt_amount: e.target.value } })} data-testid="corr-usdt" />
                </div>}
                {(corrForm.request_type === "total" || corrForm.request_type === "otro") && <div>
                  <Label className="text-xs">Total propuesto</Label>
                  <Input type="number" step="0.01" value={corrForm.proposed_data.total_amount ?? ""} onChange={(e) => setCorrForm({ ...corrForm, proposed_data: { ...corrForm.proposed_data, total_amount: e.target.value } })} />
                </div>}
                {(corrForm.request_type === "precio" || corrForm.request_type === "otro") && corrTxn?.market_type === "detalle" && (
                  <div>
                    <Label className="text-xs">Precio DOP propuesto</Label>
                    <Input type="number" step="0.0001" value={corrForm.proposed_data.rate_dop ?? ""} onChange={(e) => setCorrForm({ ...corrForm, proposed_data: { ...corrForm.proposed_data, rate_dop: e.target.value } })} />
                  </div>
                )}
                {(corrForm.request_type === "porcentaje" || corrForm.request_type === "otro") && corrTxn?.market_type === "mayor" && (
                  <div>
                    <Label className="text-xs">% propuesto</Label>
                    <Input type="number" step="0.0001" value={corrForm.proposed_data.percentage ?? ""} onChange={(e) => setCorrForm({ ...corrForm, proposed_data: { ...corrForm.proposed_data, percentage: e.target.value } })} />
                  </div>
                )}
                {(corrForm.request_type === "moneda") && <div>
                  <Label className="text-xs">Moneda propuesta</Label>
                  <Input value={corrForm.proposed_data.currency ?? ""} onChange={(e) => setCorrForm({ ...corrForm, proposed_data: { ...corrForm.proposed_data, currency: e.target.value.toUpperCase() } })} />
                </div>}
                {(corrForm.request_type === "memo" || corrForm.request_type === "otro") && <div>
                  <Label className="text-xs">Memo propuesto</Label>
                  <Input value={corrForm.proposed_data.memo ?? ""} onChange={(e) => setCorrForm({ ...corrForm, proposed_data: { ...corrForm.proposed_data, memo: e.target.value } })} />
                </div>}
                {corrForm.request_type === "fecha" && <div>
                  <Label className="text-xs">Nueva fecha y hora</Label>
                  <Input type="datetime-local" value={corrForm.proposed_data.transaction_date ?? ""} onChange={(e) => setCorrForm({ ...corrForm, proposed_data: { transaction_date: e.target.value } })} data-testid="corr-transaction-date" />
                  <p className="text-xs text-amber-700 mt-1">Si pertenece a un mes cerrado, el cierre y la apertura se recalcularan como un solo grupo.</p>
                </div>}
              </div>
            )}
            <div>
              <Label className="text-xs">Motivo *</Label>
              <Textarea value={corrForm.reason} onChange={(e) => setCorrForm({ ...corrForm, reason: e.target.value })} data-testid="corr-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrTxn(null)}>Cancelar</Button>
            <Button onClick={doCorrection} className="bg-amber-600 hover:bg-amber-700" data-testid="corr-submit">Enviar solicitud</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History */}
      <Dialog open={!!historyTxn} onOpenChange={(o) => !o && setHistoryTxn(null)}>
        <DialogContent className="max-w-2xl" data-testid="history-dialog">
          <DialogHeader><DialogTitle>Historial de cambios</DialogTitle></DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-3">
            {history.length === 0 && <p className="text-sm text-slate-500">Sin cambios registrados.</p>}
            {history.map((h) => (
              <div key={h.id} className="border border-slate-200 rounded-lg p-3">
                <div className="flex justify-between text-xs">
                  <span className="font-semibold text-slate-700">{h.action === "annul" ? "Anulación" : "Edición"} — {h.changed_by_name}</span>
                  <span className="text-slate-500">{fmt.date(h.change_date)}</span>
                </div>
                <p className="text-sm mt-1 text-slate-700"><span className="font-semibold">Motivo:</span> {h.edit_reason}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
